import { v4 as UUID_V4 } from "uuid";

import {
  MediaUploadRequest,
  MediaUploadResponse,
  BatchMediaUploadRequest,
  MediaConfig,
  MediaUploadError,
  MediaType,
} from "../types/media.types";

import axios from "../../axios";

const DEFAULT_MEDIA_CONFIG: MediaConfig = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  allowedTypes: ["image", "document"],
  allowedMimeTypes: [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
};

const validateFile = (file: File, config: MediaConfig = DEFAULT_MEDIA_CONFIG): MediaUploadError | null => {
  if (file.size > config.maxFileSize) {
    return {
      code: "FILE_TOO_LARGE",
      message: `File size exceeds maximum limit of ${config.maxFileSize / 1024 / 1024}MB`,
      fileName: file.name,
    };
  }

  const fileType = file.type.split("/")[0] as MediaType;
  if (!config.allowedTypes.includes(fileType)) {
    return {
      code: "INVALID_FILE_TYPE",
      message: `File type ${fileType} is not allowed. Allowed types: ${config.allowedTypes.join(", ")}`,
      fileName: file.name,
    };
  }

  if (!config.allowedMimeTypes.includes(file.type)) {
    return {
      code: "INVALID_MIME_TYPE",
      message: `File mime type ${file.type} is not allowed`,
      fileName: file.name,
    };
  }

  return null;
};

export const getPresignedUrl = async (fileName: string): Promise<string> => {
  const response = await axios.post("/media-persigned-url", { files: [{ name: fileName }] });
  return response.data.data[0].url;
};

export const uploadSingleMedia = async ({ file, onProgress }: MediaUploadRequest): Promise<MediaUploadResponse> => {
  const validationError = validateFile(file);
  if (validationError) {
    // throw validationError;
  }

  const fileName = `${UUID_V4()}.${file.name.split(".").pop()}`;
  const presignedUrl = await getPresignedUrl(fileName);

  try {
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress({
            fileName: file.name,
            progress: (event.loaded / event.total) * 100,
          });
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject(new Error("Upload failed"));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));

      xhr.open("PUT", presignedUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });

    return {
      url: presignedUrl.split("?")[0],
      fileName,
      fileType: file.type,
      fileSize: file.size,
    };
  } catch (error) {
    throw {
      code: "UPLOAD_FAILED",
      message: "Failed to upload file",
      fileName: file.name,
    };
  }
};

export const uploadMultipleMedia = async ({
  files,
  onProgress,
}: BatchMediaUploadRequest): Promise<MediaUploadResponse[]> => {
  const uploads = files.map((file) =>
    uploadSingleMedia({
      file,
      onProgress: (progress) => {
        if (onProgress) {
          onProgress(
            files.map((f) => ({
              fileName: f.name,
              progress: f.name === progress.fileName ? progress.progress : 0,
            }))
          );
        }
      },
    })
  );

  try {
    return await Promise.all(uploads);
  } catch (error) {
    throw {
      code: "BATCH_UPLOAD_FAILED",
      message: "Failed to upload one or more files",
      error,
    };
  }
};
